import { isTimerAction } from "../behavior-timers/protocol.js";
/**
 * Runtime resolution helpers for the layout system.
 *
 * These pure functions resolve variable bindings, evaluate style conditions
 * and resolve list bindings against InjectedData supplied by the host app.
 */

import type {
  DataBinding,
  DataSourceId,
  InjectedData,
  LayoutDocument,
  LayoutListComponentProps,
  LayoutNode,
  LayoutNodeStyle,
} from "./types.js";
import { getDataBindingSource } from "./types.js";
import { DATA_SOURCE_PATHS } from "./data-source-schema.js";
import type { BehaviorNode } from "../blueprint/types.js";

type DecimalLike = {
  toNumber: () => number;
  constructor?: { name?: string };
};

/**
 * Detects Prisma Decimal instances without importing Prisma runtime in browser bundles.
 *
 * @param value - Unknown runtime value.
 * @returns True when value behaves like a Prisma Decimal object.
 */
export function isDecimalLike(value: unknown): value is DecimalLike {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as DecimalLike;
  if (typeof candidate.toNumber !== "function") {
    return false;
  }

  const symbolTag = (candidate as Record<PropertyKey, unknown>)[
    Symbol.toStringTag
  ];
  if (symbolTag === "Decimal") {
    return true;
  }

  if (Object.prototype.toString.call(candidate) === "[object Decimal]") {
    return true;
  }

  // Fallback for runtime variants/minified constructors exposing Decimal methods.
  return typeof (candidate as { toJSON?: unknown }).toJSON === "function";
}

// ---------------------------------------------------------------------------
// Data access helpers
// ---------------------------------------------------------------------------

/**
 * Retrieves the raw value of a DataSource from the injected runtime data.
 *
 * @param data - Runtime data bag supplied by the host DataProvider.
 * @param source - The data source identifier to look up.
 * @returns The value (may be undefined if the source is not available).
 */
export function getSourceValue(
  data: InjectedData,
  source: DataSourceId,
): unknown {
  return data[source as keyof InjectedData];
}

/**
 * Resolves a dot-separated path on an object.
 *
 * @param obj - The root object.
 * @param path - Dot-separated path, e.g. "catPoid.nom".
 * @returns The resolved value, or undefined when any segment is missing.
 *
 * @example
 * resolvePath({ a: { b: 42 } }, "a.b"); // 42
 */
export function resolvePath(obj: unknown, path: string): unknown {
  if (obj == null) return undefined;
  const segments = path.split(".").filter((segment) => segment.length > 0);
  if (segments.length === 0) return obj;
  const visit = (current: unknown, index: number): unknown => {
    if (index === segments.length)
      return isDecimalLike(current) ? current.toNumber() : current;
    if (current == null || typeof current !== "object") return undefined;
    const segment = segments[index];
    // A list projection watches field values without reacting to sibling fields.
    if (segment === "*" && Array.isArray(current))
      return current.map((item) => visit(item, index + 1));
    return visit((current as Record<string, unknown>)[segment], index + 1);
  };
  return visit(obj, 0);
}

/**
 * Maps editor `currentElement` data-binding paths to runtime `layoutListRowItem`
 * paths used by {@link InjectedData} inside `layoutList` row templates.
 *
 * @param path - Binding path stored on the layout node (e.g. `currentElement.prenom`).
 * @returns Normalized dot-path against {@link InjectedData}.
 *
 * @example
 * normalizeListRowBindingPath("currentElement"); // "layoutListRowItem"
 * normalizeListRowBindingPath("currentElement.nom"); // "layoutListRowItem.nom"
 */
export function normalizeListRowBindingPath(path: string): string {
  if (path === "currentElement") {
    return "layoutListRowItem";
  }
  if (path.startsWith("currentElement.")) {
    return `layoutListRowItem.${path.slice("currentElement.".length)}`;
  }
  return path;
}

// ---------------------------------------------------------------------------
// Binding resolution
// ---------------------------------------------------------------------------

/**
 * Resolves a VariableBinding to a display-ready string.
 *
 * @param binding - The binding descriptor.
 * @param data - Runtime injected data.
 * @returns The formatted string or an empty string when data is unavailable.
 */
export function resolveBinding(
  binding: DataBinding,
  data: InjectedData,
): unknown {
  return resolvePath(data, binding.path);
}

// ---------------------------------------------------------------------------
// Style resolution
// ---------------------------------------------------------------------------

/**
 * Resolves style properties to plain CSS-ready values.
 *
 * @param style - Flat style descriptor.
 * @param _data - Runtime injected data (kept for API compatibility).
 * @returns A flat record of resolved CSS properties.
 */
export function resolveStyles(
  style: LayoutNodeStyle | undefined,
  _data: InjectedData,
): Record<string, unknown> {
  if (!style) return {};
  return { ...style };
}

export function getLayoutListDataBinding(
  nodes: LayoutNode[],
  targetNodeId: string,
): DataBinding | undefined {
  if (!isNodeInLayoutListRowTemplateContext(nodes, targetNodeId)) {
    return undefined;
  }
  const containsNodeById = (root: LayoutNode, id: string): boolean => {
    if (root.id === id) return true;
    if (root.kind === "container") {
      return root.children.some((child) => containsNodeById(child, id));
    }
    return false;
  };

  const findParentLayoutList = (tree: LayoutNode[]): LayoutNode | undefined => {
    for (const node of tree) {
      if (node.kind === "component" && node.component === "layoutList") {
        const props = (node.props as Partial<LayoutListComponentProps>) ?? {};
        const templates = [
          ...(props.rowTemplate ? [props.rowTemplate] : []),
          ...(props.rowVariants ?? []).map((variant) => variant.template),
        ];
        const hasTargetInAnyTemplate = templates.some((template) =>
          containsNodeById(template, targetNodeId),
        );
        if (hasTargetInAnyTemplate) {
          return node;
        }
      }

      if (node.kind === "container") {
        const nestedResult = findParentLayoutList(node.children);
        if (nestedResult) {
          return nestedResult;
        }
      }
    }

    return undefined;
  };

  return findParentLayoutList(nodes)?.dataBinding;
}

/**
 * Builds {@link InjectedData} scoped to the first row of a parent `layoutList`,
 * matching the runtime context that {@link LayoutList} injects per visible row.
 *
 * Used by the display editor (`PropertyPanel`, `EditorCanvasNode`) so node
 * behaviors referencing `layoutListRowItem` or `rowIndex` evaluate consistently
 * with `LayoutRenderer` preview rows.
 *
 * @param sceneChildren - Root scene nodes (not a flattened node list).
 * @param targetNodeId - Id of a node inside a `layoutList` row template.
 * @param data - Host preview or live injected data.
 * @returns Scoped data when the node sits in a row template; otherwise `data` unchanged.
 */
export function buildLayoutListRowPreviewData(
  sceneChildren: LayoutNode[],
  targetNodeId: string,
  data: InjectedData,
): InjectedData {
  const dataBinding = getLayoutListDataBinding(sceneChildren, targetNodeId);
  if (!dataBinding) {
    return data;
  }

  const sourceKey = getDataBindingSource(dataBinding);
  const rawRows = resolveBinding(dataBinding, data);
  const rows = Array.isArray(rawRows) ? rawRows : [];
  const rowItem = rows[0];

  const scoped: InjectedData = {
    ...data,
    rowIndex: 0,
    layoutListRowItem: rowItem,
  };
  if (sourceKey) {
    (scoped as unknown as Record<string, unknown>)[sourceKey] = rows as unknown;
  }
  return scoped;
}

/**
 * Resolves the backing array for a `layoutList` data binding.
 *
 * @param dataBinding - List binding configured on the `layoutList` node.
 * @param data - Host injected data.
 * @returns The bound list as an array (possibly empty).
 */
export function getLayoutListRowsFromBinding(
  dataBinding: DataBinding | undefined,
  data: InjectedData,
): unknown[] {
  if (!dataBinding) return [];
  const raw = resolveBinding(dataBinding, data);
  return Array.isArray(raw) ? raw : [];
}

/**
 * Builds {@link InjectedData} for evaluating a `setFilter` predicate at a given
 * row index, matching the scope that the legacy behavior runtime provided via
 * `getOutput` on the wired value subgraph.
 *
 * @param data - Base injected data.
 * @param dataBinding - `layoutList` list binding (source of row items).
 * @param rowIndex - Index into the bound list (original index in the full array).
 * @param rowsOverride - Optional computed source replacing the bound list.
 * @returns Scoped data with `rowIndex`, `layoutListRowItem`, and the source list.
 */
export function buildLayoutListFilterScopedData(
  data: InjectedData,
  dataBinding: DataBinding | undefined,
  rowIndex: number,
  rowsOverride?: unknown[],
): InjectedData {
  const rows = rowsOverride ?? getLayoutListRowsFromBinding(dataBinding, data);
  const index = Math.max(0, Math.round(rowIndex));
  const sourceKey = dataBinding ? getDataBindingSource(dataBinding) : undefined;
  const scoped: InjectedData = {
    ...data,
    rowIndex: index,
    layoutListRowItem: rows[index],
  };
  if (sourceKey) {
    (scoped as unknown as Record<string, unknown>)[sourceKey] = rows as unknown;
  }
  return scoped;
}

/**
 * Builds {@link InjectedData} for evaluating a `setSort` comparator between two
 * row indices, including `iterationItemA` / `iterationItemB` for comparator
 * operator bindings and `sortAIndex` / `sortBIndex` for layout-list variables.
 *
 * @param data - Base injected data.
 * @param dataBinding - `layoutList` list binding.
 * @param sortAIndex - Left/original index in the bound list.
 * @param sortBIndex - Right/original index in the bound list.
 * @param rowsOverride - Optional computed source replacing the bound list.
 * @returns Scoped data for comparator expression resolution.
 */
export function buildLayoutListSortScopedData(
  data: InjectedData,
  dataBinding: DataBinding | undefined,
  sortAIndex: number,
  sortBIndex: number,
  rowsOverride?: unknown[],
): InjectedData {
  const rows = rowsOverride ?? getLayoutListRowsFromBinding(dataBinding, data);
  const a = Math.max(0, Math.round(sortAIndex));
  const b = Math.max(0, Math.round(sortBIndex));
  const sourceKey = dataBinding ? getDataBindingSource(dataBinding) : undefined;
  const scoped: InjectedData = {
    ...data,
    sortAIndex: a,
    sortBIndex: b,
    iterationItemA: rows[a],
    iterationItemB: rows[b],
  };
  if (sourceKey) {
    (scoped as unknown as Record<string, unknown>)[sourceKey] = rows as unknown;
  }
  return scoped;
}

// ---------------------------------------------------------------------------
// Data source usage analysis
// ---------------------------------------------------------------------------

/**
 * Sources that are produced by the renderer/runtime context rather than by the
 * host application data provider.
 *
 * These sources may be referenced by a document, but callers must not use this
 * analysis to request/inject them from Electron, NestJS or another host.
 */
const CONTEXT_DATA_SOURCES = [
  "currentNode",
  "rowIndex",
  "layoutListRowItem",
  "sortAIndex",
  "sortBIndex",
  "listTransitionSignal",
  "iterationItem",
  "iterationIndex",
  "iterationItemA",
  "iterationItemB",
] as const satisfies readonly DataSourceId[];

export type ContextDataSourceId = (typeof CONTEXT_DATA_SOURCES)[number];
export type InjectableDataSourceId = Exclude<DataSourceId, ContextDataSourceId>;

/**
 * Boolean usage map over every host-injectable {@link DataSourceId}.
 *
 * Each injectable key from {@link InjectedData} is present and set to `true`
 * when the source is referenced anywhere in the document (node binding, node
 * behavior graph or scene behavior graph), `false` otherwise. Runtime context
 * sources are intentionally excluded.
 */
export type DataSourceUsage = Record<InjectableDataSourceId, boolean>;

/**
 * Inspects every reference to a {@link DataSourceId} inside a {@link LayoutDocument}
 * and returns a flat boolean map telling which sources are actually consumed.
 *
 * The traversal covers:
 * - Every {@link LayoutNode}'s `dataBinding.path` first segment.
 * - Every behavior graph attached to a node (`node.behavior.nodes[]`),
 *   reading `objectSource` from variable / event / operator (`get`) nodes.
 * - Every behavior graph inside `scene.catalog` entries (animations, events, functions, variables).
 *
 * Nodes nested inside containers and inside `layoutList` templates
 * (`rowTemplate` and every `rowVariants[].template`) are walked recursively.
 *
 * @param document - The layout document to inspect.
 * @returns A {@link DataSourceUsage} record. Every host-injectable source key
 *   is present; sources that are not referenced are set to `false`.
 *
 * @example
 * const usage = getUsedDataSources(layoutDocument);
 * if (usage.currentAthlete) {
 *   // ... fetch / inject athlete data
 * }
 */
export function getUsedDataSources(document: LayoutDocument): DataSourceUsage {
  const contextSources = new Set<string>(CONTEXT_DATA_SOURCES);
  const sourceKeys = (Object.keys(DATA_SOURCE_PATHS) as DataSourceId[]).filter(
    (source): source is InjectableDataSourceId => !contextSources.has(source),
  );
  const usage = sourceKeys.reduce((acc, key) => {
    acc[key] = false;
    return acc;
  }, {} as DataSourceUsage);

  const validSources = new Set<string>(sourceKeys);

  const markSource = (source: string | undefined | null): void => {
    if (!source) return;
    if (validSources.has(source)) {
      usage[source as InjectableDataSourceId] = true;
    }
  };

  const visitBehaviorGraph = (
    graph: { nodes: BehaviorNode[] } | undefined,
  ): void => {
    if (!graph) return;
    for (const node of graph.nodes) {
      if (
        (node.type === "operator" && node.operator === "readTimer") ||
        (node.type === "action" &&
          node.kind === "set" &&
          isTimerAction(node.key))
      ) {
        markSource("currentMeet");
        markSource("behaviorTimers");
      }
      // VariableNode, EventNode and OperatorNode (`get`) all expose an optional
      // `objectSource` field typed as `string` at runtime but storing a
      // DataSourceId value.
      const candidate = (node as { objectSource?: unknown }).objectSource;
      if (typeof candidate === "string") {
        markSource(candidate);
      }
    }
  };

  const visitLayoutNode = (node: LayoutNode): void => {
    markSource(getDataBindingSource(node.dataBinding));
    visitBehaviorGraph(node.behavior);

    if (node.kind === "container") {
      for (const child of node.children) {
        visitLayoutNode(child);
      }
      return;
    }

    if (node.kind === "component" && node.component === "layoutList") {
      const props = (node.props as Partial<LayoutListComponentProps>) ?? {};
      if (props.rowTemplate) {
        visitLayoutNode(props.rowTemplate);
      }
      for (const variant of props.rowVariants ?? []) {
        if (variant.template) {
          visitLayoutNode(variant.template);
        }
      }
    }
  };

  const catalog = document.scene.catalog;
  if (catalog) {
    for (const anim of catalog.animations) visitBehaviorGraph(anim.graph);
    for (const evt of catalog.customEvents)
      visitBehaviorGraph(evt.triggerGraph);
    for (const fn of catalog.functions) visitBehaviorGraph(fn.graph);
    for (const v of catalog.variables) visitBehaviorGraph(v.graph);
  }
  for (const node of document.scene.children) {
    visitLayoutNode(node);
  }

  return usage;
}

export function isNodeInLayoutListRowTemplateContext(
  nodes: LayoutNode[],
  targetNodeId: string,
): boolean {
  const containsNodeById = (root: LayoutNode, id: string): boolean => {
    if (root.id === id) return true;
    if (root.kind === "container") {
      return root.children.some((child) => containsNodeById(child, id));
    }
    return false;
  };

  for (const node of nodes) {
    if (node.kind === "component" && node.component === "layoutList") {
      const props = (node.props as Partial<LayoutListComponentProps>) ?? {};
      const templates = [
        ...(props.rowTemplate ? [props.rowTemplate] : []),
        ...(props.rowVariants ?? []).map((variant) => variant.template),
      ];
      const hasTargetInAnyTemplate = templates.some((template) =>
        containsNodeById(template, targetNodeId),
      );
      if (hasTargetInAnyTemplate) {
        return true;
      }
    }
    if (
      node.kind === "container" &&
      isNodeInLayoutListRowTemplateContext(node.children, targetNodeId)
    ) {
      return true;
    }
  }
  return false;
}
